import {
	AddComment,
	Article,
	ConnectedTv,
	Edit,
	Event,
	Face,
	Folder,
	Gavel,
	Group,
	Home,
	HowToReg,
	HowToVote,
	Image,
	InterpreterMode,
	LowPriority,
	Map as MapIcon,
	MusicNote,
	Person,
	Poll,
	QuestionMark,
	RateReview,
	RecordVoiceOver,
	Search,
	Subject,
	UploadFile,
} from "@mui/icons-material";
import { Avatar as MuiAvatar, Skeleton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import type { Maybe } from "gql";
import i18n from "i18n";
import FilePdfBoxIcon from "./svg/file-pdf-box.svg?react";
import MicrosoftExcelIcon from "./svg/microsoft-excel.svg?react";
import MicrosoftPowerpointIcon from "./svg/microsoft-powerpoint.svg?react";
import MicrosoftWordIcon from "./svg/microsoft-word.svg?react";
import VideoBoxIcon from "./svg/video-box.svg?react";

const getLetter = (index: number) => {
	const f = String.fromCharCode(65 + (index % 26));
	return index >= 26 ? String.fromCharCode(64 + Math.floor(index / 26)) + f : f;
};

const IconId = ({
	mimeId,
	index,
	name,
	avatar,
	child,
}: {
	mimeId: Maybe<string | undefined>;
	index?: number;
	name?: string;
	avatar?: boolean;
	child?: boolean;
}) => {
	switch (mimeId) {
		case "wiki/search":
			return <Search />;
		case "wiki/home":
			return <Home />;
		case "wiki/group":
			return <Group />;
		case "wiki/event":
			return <Event />;
		case "wiki/folder":
			return name?.[0] ? (
				<Box sx={{ color: "inherit", minWidth: "25px" }}>
					<Folder
						sx={{
							fontSize: 32,
							position: avatar ? "relative" : "absolute",
							marginLeft: avatar ? "0px" : "-3px",
							marginTop: avatar ? "0px" : "-3px",
						}}
					/>
					<Typography
						sx={{
							color: avatar
								? child
									? "secondary.main"
									: "primary.main"
								: "common.white",
							position: avatar ? "absolute" : "relative",
							top: avatar ? 8 : 3,
							left: avatar ? 16 : 8,
						}}
					>
						<b>{name?.[0]}</b>
					</Typography>
				</Box>
			) : (
				<Folder />
			);
		case "wiki/document":
			return <Article />;
		case "wiki/file":
			return <UploadFile />;
		case "wiki/user":
			return <Person />;
		case "text/plain":
			return <Subject />;
		case "vote/policy":
			return index !== undefined && index !== -1 ? (
				avatar ? (
					<Typography fontSize={24} sx={{ color: "inherit" }}>
						{getLetter(index)}
					</Typography>
				) : (
					<MuiAvatar
						sx={{
							width: 24,
							height: 24,
							color: "currentColor",
							bgcolor: "currentColor",
						}}
					>
						<Typography
							fontSize={18}
							sx={{
								color: avatar ? "secondary.main" : "common.white",
							}}
						>
							{getLetter(index)}
						</Typography>
					</MuiAvatar>
				)
			) : (
				<Gavel />
			);
		case "vote/position":
			return <HowToReg />;
		case "vote/candidate":
			return <Face />;
		case "vote/question":
			return <QuestionMark />;
		case "vote/comment":
			return <AddComment />;
		case "vote/change":
			return index !== undefined && index !== -1 ? (
				avatar ? (
					<Typography fontSize={24} sx={{ color: "inherit" }}>
						{index + 1}
					</Typography>
				) : (
					<MuiAvatar
						sx={{
							width: 24,
							height: 24,
							color: "currentColor",
							bgcolor: "currentColor",
						}}
					>
						<Typography
							fontSize={18}
							sx={{
								color: avatar ? "secondary.main" : "common.white",
							}}
						>
							{index + 1}
						</Typography>
					</MuiAvatar>
				)
			) : (
				<RateReview />
			);
		case "vote/poll":
			return <Poll />;
		case "speak/list":
			return <InterpreterMode />;
		case "application/pdf":
			return <FilePdfBoxIcon fill="currentColor" height="24" width="24" />;
		case "app/home":
			return <Home />;
		case "app/editor":
			return <Edit />;
		case "app/sort":
			return <LowPriority />;
		case "app/vote":
			return <HowToVote />;
		case "app/speak":
			return <RecordVoiceOver />;
		case "app/search":
			return <Search />;
		case "app/member":
			return <Group />;
		case "app/folder":
			return <Folder />;
		case "app/screen":
			return <ConnectedTv />;
		case "app/map":
		case "map/map":
			return <MapIcon />;
		case undefined:
			return <Skeleton variant="circular" width={24} height={24} />;
		default:
	}

	// eslint-disable-next-line jsx-a11y/alt-text
	if (mimeId?.includes("image/")) return <Image />;
	if (mimeId?.includes("audio/")) return <MusicNote />;
	if (mimeId?.includes("video/"))
		return <VideoBoxIcon fill="currentColor" height="24" width="24" />;
	if (mimeId?.includes("spreadsheet"))
		return <MicrosoftExcelIcon fill="currentColor" height="24" width="24" />;
	if (mimeId?.includes("presentation"))
		return (
			<MicrosoftPowerpointIcon fill="currentColor" height="24" width="24" />
		);
	if (mimeId?.includes("document"))
		return <MicrosoftWordIcon fill="currentColor" height="24" width="24" />;

	return <QuestionMark />;
};

const getName = (mimeId?: string): string => {
	switch (mimeId) {
		case "wiki/group":
			return i18n.t("mime.group");
		case "wiki/event":
			return i18n.t("mime.event");
		case "wiki/folder":
			return i18n.t("mime.folder");
		case "wiki/document":
			return i18n.t("mime.document");
		case "wiki/file":
			return i18n.t("mime.file");
		case "wiki/user":
			return i18n.t("mime.person");
		case "text/plain":
			return i18n.t("mime.document");
		case "vote/policy":
			return i18n.t("mime.policy");
		case "vote/position":
			return i18n.t("mime.position");
		case "vote/change":
			return i18n.t("mime.amendment");
		case "vote/candidate":
			return i18n.t("mime.candidate");
		case "vote/question":
			return i18n.t("mime.questionType");
		case "vote/comment":
			return i18n.t("mime.comment");
		case "speak/list":
			return i18n.t("mime.speakerList");
		case "app/editor":
			return i18n.t("mime.editor");
		case "app/sort":
			return i18n.t("mime.sort");
		case "app/speak":
			return i18n.t("mime.speak");
		case "app/vote":
			return i18n.t("mime.vote");
		case "app/member":
			return i18n.t("mime.members");
		case "app/map":
		case "map/map":
			return i18n.t("mime.map");
		default:
			return i18n.t("mime.unknown");
	}
};

export { IconId, getLetter, getName };
